import { type SVGProps } from 'react'

/**
 * design/js/icons.jsx の lucide サブセットから、T-B2 シェルで使う 14 種に絞って TS 移植。
 *
 *   Sidenav: home / vault / send / key / shield / settings / user / lock / logout
 *   Topbar : panel-left / search / bell
 *   Notice / Empty 既定: info / alert
 *
 * 後続 (T-C1+) で必要なアイコンは追加していく。design/js/icons.jsx と同じ d 属性を
 * そのまま使う (見た目を一致させるため)。stroke 属性は theme.css 側の CSS で
 * currentColor を継承する。
 */

export type IconName =
  | 'home'
  | 'vault'
  | 'send'
  | 'key'
  | 'shield'
  | 'settings'
  | 'user'
  | 'lock'
  | 'logout'
  | 'panel-left'
  | 'search'
  | 'bell'
  | 'info'
  | 'alert'
  | 'arrow-left'
  | 'users'
  | 'mail'
  | 'chevron-right'
  | 'chevron-down'
  | 'chevron-up'
  | 'check'
  // T-D-Vault-A 追加 (design/js/icons.jsx と同じ d 属性)
  | 'plus'
  | 'copy'
  | 'external'
  | 'eye'
  | 'eye-off'
  | 'edit'
  | 'trash'
  | 'globe'
  | 'credit-card'
  | 'file-text'
  | 'id-card'
  | 'save'
  | 'star'
  // T-D-Reports 追加
  | 'unlock'
  // T-D-Tools 追加
  | 'upload'
  | 'download'
  // T-D-Home 追加 (design/js/icons.jsx と同一)
  | 'shield-check'
  | 'shield-alert'
  | 'arrow-right'
  | 'refresh'
  // T-D-Send 追加
  | 'share'
  | 'link'
  // T-D-Vault-Folder 追加
  | 'folder'
  // T-D-Emergency 履歴 button
  | 'clock'

type SvgProps = SVGProps<SVGSVGElement>

const Svg = ({ children, ...rest }: SvgProps): JSX.Element => (
  <svg
    width={16}
    height={16}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...rest}
  >
    {children}
  </svg>
)

const ICONS: Record<IconName, (props?: SvgProps) => JSX.Element> = {
  home: p => (
    <Svg {...p}>
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <path d="M9 22V12h6v10" />
    </Svg>
  ),
  vault: p => (
    <Svg {...p}>
      <path d="M5 4h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
      <circle cx={12} cy={12} r={3} />
      <line x1={12} y1={9} x2={12} y2={7} />
      <line x1={12} y1={17} x2={12} y2={15} />
      <line x1={9} y1={12} x2={7} y2={12} />
      <line x1={17} y1={12} x2={15} y2={12} />
    </Svg>
  ),
  send: p => (
    <Svg {...p}>
      <path d="m22 2-7 20-4-9-9-4Z" />
      <path d="M22 2 11 13" />
    </Svg>
  ),
  key: p => (
    <Svg {...p}>
      <circle cx={7.5} cy={15.5} r={5.5} />
      <path d="m21 2-9.6 9.6" />
      <line x1={15.5} y1={7.5} x2={19} y2={4} />
      <line x1={13} y1={5} x2={16} y2={8} />
    </Svg>
  ),
  shield: p => (
    <Svg {...p}>
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
    </Svg>
  ),
  settings: p => (
    <Svg {...p}>
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2" />
      <circle cx={12} cy={12} r={3} />
    </Svg>
  ),
  user: p => (
    <Svg {...p}>
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx={12} cy={7} r={4} />
    </Svg>
  ),
  lock: p => (
    <Svg {...p}>
      <rect width={18} height={11} x={3} y={11} rx={2} ry={2} />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </Svg>
  ),
  logout: p => (
    <Svg {...p}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5" />
      <line x1={21} y1={12} x2={9} y2={12} />
    </Svg>
  ),
  'panel-left': p => (
    <Svg {...p}>
      <rect width={18} height={18} x={3} y={3} rx={2} />
      <line x1={9} y1={3} x2={9} y2={21} />
    </Svg>
  ),
  search: p => (
    <Svg {...p}>
      <circle cx={11} cy={11} r={8} />
      <line x1={21} y1={21} x2={16.65} y2={16.65} />
    </Svg>
  ),
  bell: p => (
    <Svg {...p}>
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </Svg>
  ),
  info: p => (
    <Svg {...p}>
      <circle cx={12} cy={12} r={10} />
      <line x1={12} y1={16} x2={12} y2={12} />
      <line x1={12} y1={8} x2={12.01} y2={8} />
    </Svg>
  ),
  alert: p => (
    <Svg {...p}>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1={12} y1={9} x2={12} y2={13} />
      <line x1={12} y1={17} x2={12.01} y2={17} />
    </Svg>
  ),
  // T-C4 LegalPage の戻るボタン用 (design/js/icons.jsx:41 と同一)
  'arrow-left': p => (
    <Svg {...p}>
      <line x1={19} y1={12} x2={5} y2={12} />
      <path d="m12 19-7-7 7-7" />
    </Svg>
  ),
  // T-C5a ForgotPage の経路カード用 (design/js/icons.jsx と同一)
  users: p => (
    <Svg {...p}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx={9} cy={7} r={4} />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </Svg>
  ),
  mail: p => (
    <Svg {...p}>
      <rect width={20} height={16} x={2} y={4} rx={2} />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </Svg>
  ),
  'chevron-right': p => (
    <Svg {...p}>
      <path d="m9 18 6-6-6-6" />
    </Svg>
  ),
  'chevron-down': p => (
    <Svg {...p}>
      <path d="m6 9 6 6 6-6" />
    </Svg>
  ),
  'chevron-up': p => (
    <Svg {...p}>
      <path d="m18 15-6-6-6 6" />
    </Svg>
  ),
  // T-C5c EmergencyContactPage の sent 状態 success アイコン用 (design/js/icons.jsx と同一)
  check: p => (
    <Svg {...p}>
      <path d="M20 6 9 17l-5-5" />
    </Svg>
  ),
  // T-D-Vault-A 以降の保管庫 UI 用 (design/js/icons.jsx と同一)
  plus: p => (
    <Svg {...p}>
      <line x1={12} y1={5} x2={12} y2={19} />
      <line x1={5} y1={12} x2={19} y2={12} />
    </Svg>
  ),
  copy: p => (
    <Svg {...p}>
      <rect width={14} height={14} x={8} y={8} rx={2} ry={2} />
      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
    </Svg>
  ),
  external: p => (
    <Svg {...p}>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <path d="m21 3-9 9" />
      <path d="M15 3h6v6" />
    </Svg>
  ),
  eye: p => (
    <Svg {...p}>
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7" />
      <circle cx={12} cy={12} r={3} />
    </Svg>
  ),
  'eye-off': p => (
    <Svg {...p}>
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <line x1={2} y1={2} x2={22} y2={22} />
    </Svg>
  ),
  edit: p => (
    <Svg {...p}>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z" />
    </Svg>
  ),
  trash: p => (
    <Svg {...p}>
      <path d="M3 6h18" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </Svg>
  ),
  globe: p => (
    <Svg {...p}>
      <circle cx={12} cy={12} r={10} />
      <path d="M2 12h20" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </Svg>
  ),
  'credit-card': p => (
    <Svg {...p}>
      <rect width={20} height={14} x={2} y={5} rx={2} />
      <line x1={2} y1={10} x2={22} y2={10} />
    </Svg>
  ),
  'file-text': p => (
    <Svg {...p}>
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5z" />
      <path d="M14 2v6h6" />
      <line x1={8} y1={13} x2={16} y2={13} />
      <line x1={8} y1={17} x2={16} y2={17} />
      <line x1={8} y1={9} x2={10} y2={9} />
    </Svg>
  ),
  'id-card': p => (
    <Svg {...p}>
      <rect width={20} height={14} x={2} y={5} rx={2} />
      <line x1={8} y1={10} x2={8} y2={14} />
      <line x1={11} y1={10} x2={16} y2={10} />
      <line x1={11} y1={14} x2={16} y2={14} />
      <circle cx={8} cy={11.5} r={1.5} />
    </Svg>
  ),
  save: p => (
    <Svg {...p}>
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <path d="M17 21v-8H7v8" />
      <path d="M7 3v5h8" />
    </Svg>
  ),
  star: p => (
    <Svg {...p}>
      <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.12 2.12 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />
    </Svg>
  ),
  // T-D-Tools 追加 (design/js/icons.jsx と同一 d 属性)
  upload: p => (
    <Svg {...p}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="m17 8-5-5-5 5" />
      <line x1={12} y1={3} x2={12} y2={15} />
    </Svg>
  ),
  download: p => (
    <Svg {...p}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="m7 10 5 5 5-5" />
      <line x1={12} y1={15} x2={12} y2={3} />
    </Svg>
  ),
  // T-D-Reports 追加 (design/js/icons.jsx と同一 d 属性)
  unlock: p => (
    <Svg {...p}>
      <rect width={18} height={11} x={3} y={11} rx={2} ry={2} />
      <path d="M7 11V7a5 5 0 0 1 9.9-1" />
    </Svg>
  ),
  // T-D-Home 追加 (design/js/icons.jsx と同一 d 属性)
  'shield-check': p => (
    <Svg {...p}>
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
      <path d="m9 12 2 2 4-4" />
    </Svg>
  ),
  'shield-alert': p => (
    <Svg {...p}>
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
      <line x1={12} y1={8} x2={12} y2={12} />
      <line x1={12} y1={16} x2={12.01} y2={16} />
    </Svg>
  ),
  'arrow-right': p => (
    <Svg {...p}>
      <line x1={5} y1={12} x2={19} y2={12} />
      <path d="m12 5 7 7-7 7" />
    </Svg>
  ),
  refresh: p => (
    <Svg {...p}>
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M8 16H3v5" />
    </Svg>
  ),
  // T-D-Vault-Folder 追加 (design/js/icons.jsx と同一 d 属性)
  folder: p => (
    <Svg {...p}>
      <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
    </Svg>
  ),
  clock: p => (
    <Svg {...p}>
      <circle cx={12} cy={12} r={10} />
      <polyline points="12 6 12 12 16 14" />
    </Svg>
  ),
  // T-D-Send 追加 (design/js/icons.jsx と同一 d 属性)
  share: p => (
    <Svg {...p}>
      <circle cx={18} cy={5} r={3} />
      <circle cx={6} cy={12} r={3} />
      <circle cx={18} cy={19} r={3} />
      <line x1={8.59} y1={13.51} x2={15.42} y2={17.49} />
      <line x1={15.41} y1={6.51} x2={8.59} y2={10.49} />
    </Svg>
  ),
  link: p => (
    <Svg {...p}>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </Svg>
  ),
}

export interface IconProps extends SvgProps {
  name: IconName
}

export const Icon = ({ name, ...rest }: IconProps): JSX.Element => {
  const render = ICONS[name]

  return render(rest)
}

/** name の存在チェック (TS の網羅性チェックより緩い・実行時 fallback 用)。 */
export const ICON_NAMES = Object.keys(ICONS) as readonly IconName[]
